/**
 * Motion vocabulary for the Mentara manta (specs/012-manta-character/DESIGN.md).
 * Pure data and pure functions: no React, no DOM, no clock. `MantaCharacter`
 * reads the tempo through CSS variables; a driver (the preview, later the hero)
 * moves the character along a path with the hop helpers below.
 *
 * v2 (2026-10-06): the rig runs continuous oscillators, so states only change
 * amplitudes; the driver uses a C2 hop curve, a low-passed bank that fades to
 * level through vertical tangents (the hairpin), and a hover offset that keeps
 * the body clear of a ring and its flag. v1 is frozen under `./v1/`.
 */

export type MantaState = 'idle' | 'swim' | 'arrive' | 'master' | 'finale' | 'nod';

export const MANTA_STATES: readonly MantaState[] = ['idle', 'swim', 'arrive', 'master', 'finale', 'nod'];

/** Every duration in ms at speed 1. The owner asked for hops of 0.6 to 0.9s and a full run near 9 to 10s. */
export const MANTA_TEMPO = {
  /** Idle breathing cycle (bob, slow wing roll, tail sway). */
  idleCycleMs: 3200,
  /** Blink period while idle. */
  blinkEveryMs: 4600,
  /** One wing beat while swimming. The oscillator never stops, so phase is continuous across states. */
  swimCycleMs: 640,
  /** Shortest and longest hop between two stops. */
  hopMinMs: 600,
  hopMaxMs: 900,
  /** A hop this long (in multiples of the character's width) costs hopMaxMs. */
  hopMaxWidths: 7,
  /** How far into a hop the character switches from `swim` to `arrive` (wings spread to brake). */
  arriveAt: 0.78,
  /** How long `arrive` runs; the swim amplitudes decay over the same time. */
  arriveMs: 320,
  /** The "master it" flourish: nod, wing flick, tassel bounce, happy squint. */
  masterMs: 450,
  /** A small nod: one practice rep landed. */
  nodMs: 260,
  /** Inside the flourish: when the ring's check lands and when its flag pops. */
  checkAtMs: 110,
  flagAtMs: 230,
  /** The finale loop and cap tip after the eighth stop. */
  finaleMs: 1400,
  /** A heading flip (scaleX 1 -> -1) and a rig bank change settle over these. */
  turnMs: 220,
  bankMs: 220,
  /** State amplitude changes (wing beat, tassel drag, body lift) blend over this. */
  blendMs: 240,
  /** Eye glance toward the cursor. */
  glanceMs: 140,
  /** Largest roll in degrees while banking into a curve. */
  maxBankDeg: 11,
  /** Time constant of the bank low-pass in the driver. */
  bankTauMs: 90,
} as const;

/** Easing shared by every hop: a quick launch, a long soft brake (the manta glides in). */
export const SWIM_EASING = 'cubic-bezier(.42,.04,.18,1)';
export const SNAP_EASING = 'cubic-bezier(.2,.8,.2,1)';

export const MANTA_VIEWBOX = { width: 744, height: 644 } as const;
export const MANTA_ASPECT = MANTA_VIEWBOX.height / MANTA_VIEWBOX.width;

/**
 * How far the drawing reaches past its own viewBox, as fractions of the width,
 * measured by pausing every state's animations and sweeping their progress
 * (DESIGN.md §9, 2026-10-06; re-measured for the v2.2 circular finale loop: 0.41 / 0.47 / 0.367 / 0.205). The finale's loop sets these; every other state
 * stays within 0.12 on any side. A container that must not clip the character
 * keeps at least this much clear around the box, or lets it overflow.
 */
export const MANTA_SAFE = { top: 0.42, right: 0.48, bottom: 0.37, left: 0.21 } as const;

/** Hop time by distance, in ms at speed 1. `distance` and `width` share a unit. */
export function hopMs(distance: number, width: number): number {
  const w = Math.max(1, width);
  const t = Math.min(1, Math.max(0, distance / (w * MANTA_TEMPO.hopMaxWidths)));
  return Math.round(MANTA_TEMPO.hopMinMs + (MANTA_TEMPO.hopMaxMs - MANTA_TEMPO.hopMinMs) * t);
}

export interface Pose {
  /** -1 = facing left (mirrored), 1 = facing right (the logo's own pose). */
  facing: -1 | 1;
  /** Screen-space roll in degrees, positive = nose down (clockwise). */
  bank: number;
}

/**
 * Facing and bank from a path tangent (screen axes, y down). The logo faces
 * right, so a leftward tangent mirrors the character; the bank is the tangent's
 * slope toward the direction of travel, clamped to `maxBankDeg`.
 */
export function poseFromTangent(dx: number, dy: number, maxBank = MANTA_TEMPO.maxBankDeg): Pose {
  const facing: -1 | 1 = dx < 0 ? -1 : 1;
  const len = Math.hypot(dx, dy) || 1;
  // slope along the direction of travel; a mirrored character rolls the other way on screen
  const slope = (dy / len) * facing;
  const bank = Math.max(-maxBank, Math.min(maxBank, (Math.asin(Math.max(-1, Math.min(1, slope))) * 180) / Math.PI));
  return { facing, bank: Math.round(bank * 10) / 10 || 0 };
}

const smoothstep = (a: number, b: number, x: number) => {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

/**
 * The bank a driver should steer toward this frame. Like `poseFromTangent`, but
 * the roll fades to level as the tangent turns vertical (so the sign change at a
 * hairpin is continuous instead of a snap from +max to -max) and scales with
 * `speedFactor` (0..1, normalised speed) so it eases in from rest and out into
 * the stop. Feed the result through `lowPass`.
 */
export function bankTarget(
  dx: number,
  dy: number,
  speedFactor = 1,
  opts: { maxBank?: number; fadeVertical?: boolean; facing?: -1 | 1 } = {},
): Pose {
  const maxBank = opts.maxBank ?? MANTA_TEMPO.maxBankDeg;
  const len = Math.hypot(dx, dy) || 1;
  // on a vertical layout the caller keeps its facing and lets the body pitch into the climb or dive
  const facing: -1 | 1 = opts.facing ?? (dx < 0 ? -1 : 1);
  const slope = Math.max(-1, Math.min(1, (dy / len) * facing));
  const raw = Math.max(-maxBank, Math.min(maxBank, (Math.asin(slope) * 180) / Math.PI));
  const verticalFade = opts.fadeVertical === false ? 1 : smoothstep(0.12, 0.45, Math.abs(dx) / len);
  const speed = Math.max(0, Math.min(1, speedFactor));
  return { facing, bank: Math.round(raw * verticalFade * speed * 100) / 100 || 0 };
}

/** First-order low-pass: moves `current` toward `target` with time constant `tauMs`. Frame-rate independent. */
export function lowPass(current: number, target: number, dtMs: number, tauMs = MANTA_TEMPO.bankTauMs): number {
  if (dtMs <= 0) return current;
  const k = 1 - Math.exp(-dtMs / Math.max(1, tauMs));
  return current + (target - current) * k;
}

/** Warp exponent for the hop curve: >1 moves the speed peak earlier (quick launch, long glide). */
const HOP_WARP = 1.3;

/**
 * Hop progress 0..1 as a function of normalised time 0..1. Quintic Hermite:
 * zero velocity and zero acceleration at the stop, zero acceleration at the
 * start, with `v0` the normalised launch speed (velocity in hop lengths per hop
 * duration), so a hop that interrupts another keeps its momentum. With v0 = 0
 * it is smootherstep in warped time: the manta launches quickly and glides in.
 */
export function hopProgress(t: number, v0 = 0): number {
  const x = Math.max(0, Math.min(1, t));
  const w = 1 - Math.pow(1 - x, HOP_WARP);
  const a = launchSlope(v0);
  return quintic(w, a);
}

/** d(hopProgress)/dt, in hop lengths per hop duration. */
export function hopVelocity(t: number, v0 = 0): number {
  const x = Math.max(0, Math.min(1, t));
  if (x >= 1) return 0;
  const w = 1 - Math.pow(1 - x, HOP_WARP);
  const dw = HOP_WARP * Math.pow(1 - x, HOP_WARP - 1);
  return quinticD(w, launchSlope(v0)) * dw;
}

function launchSlope(v0: number) {
  return Math.max(-1, Math.min(2, v0 / HOP_WARP));
}
function quintic(t: number, a: number) {
  return a * t + (10 - 6 * a) * t ** 3 + (-15 + 8 * a) * t ** 4 + (6 - 3 * a) * t ** 5;
}
function quinticD(t: number, a: number) {
  return a + 3 * (10 - 6 * a) * t ** 2 + 4 * (-15 + 8 * a) * t ** 3 + 5 * (6 - 3 * a) * t ** 4;
}

/** Peak of hopVelocity (v0 = 0), used to normalise speed for the bank. */
export const HOP_PEAK_VELOCITY = (() => {
  let peak = 0;
  for (let i = 0; i <= 200; i++) peak = Math.max(peak, hopVelocity(i / 200));
  return peak;
})();

/**
 * Where the body's anchor sits relative to a ring it hovers over, in ring radii:
 * above the ring, and a little behind the direction it faces, so the near wing
 * tip (which reaches forward and down) clears the flag badge at the ring's upper
 * right (or upper left when mirrored). Checked against the rig's extents with
 * the wing at full downstroke and the master nod's 1.05 scale (DESIGN.md §9).
 */
export function hoverOffset(facing: -1 | 1, ringR: number): { dx: number; dy: number } {
  return { dx: -0.5 * ringR * facing, dy: -2.8 * ringR };
}

/** Eye glance from a hovering body toward the ring below and ahead of it. */
export const HOVER_GLANCE = { x: 0.5, y: 0.9 } as const;

export interface PathPoint {
  x: number;
  y: number;
}

/** Minimal shape of an SVGPathElement, so the sampler is testable without a DOM. */
export interface PathLike {
  getTotalLength(): number;
  getPointAtLength(len: number): PathPoint;
}

export interface PathSample extends PathPoint, Pose {
  /** 0..1 along the hop */
  offset: number;
}

/**
 * Samples a hop along `path` from `fromLen` to `toLen` (path lengths, either
 * direction). Each sample carries the point, the facing and the bank, so the
 * caller can build WAAPI keyframes (`translate(x,y) rotate(bank)`) for a wrapper
 * and schedule facing flips for the rig. Bank is smoothed over neighbouring
 * samples so a tight curve does not twitch. (v1 driver; the v2 driver samples
 * the path per frame with `tangentAt` instead.)
 */
export function samplePath(path: PathLike, fromLen: number, toLen: number, samples = 24): PathSample[] {
  const total = path.getTotalLength();
  const clamp = (v: number) => Math.max(0, Math.min(total, v));
  const n = Math.max(2, samples);
  const raw: PathSample[] = [];
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    const len = clamp(fromLen + (toLen - fromLen) * t);
    const p = path.getPointAtLength(len);
    const dir = toLen >= fromLen ? 1 : -1;
    const a = path.getPointAtLength(clamp(len - 2 * dir));
    const b = path.getPointAtLength(clamp(len + 2 * dir));
    const pose = poseFromTangent(b.x - a.x, b.y - a.y);
    raw.push({ x: p.x, y: p.y, offset: t, ...pose });
  }
  // the bank eases in from level and back to level, and is averaged with its neighbours
  return raw.map((s, i) => {
    const prev = raw[Math.max(0, i - 1)].bank;
    const next = raw[Math.min(n - 1, i + 1)].bank;
    const edge = Math.min(1, Math.min(i, n - 1 - i) / 3);
    return { ...s, bank: Math.round(((prev + s.bank + next) / 3) * edge * 10) / 10 };
  });
}

/** Point and unit tangent (in the direction `dir`) at `len` along the path. */
export function tangentAt(path: PathLike, len: number, dir: 1 | -1, total = path.getTotalLength()): PathPoint & { dx: number; dy: number } {
  const clamp = (v: number) => Math.max(0, Math.min(total, v));
  const p = path.getPointAtLength(clamp(len));
  const a = path.getPointAtLength(clamp(len - 3 * dir));
  const b = path.getPointAtLength(clamp(len + 3 * dir));
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const n = Math.hypot(dx, dy) || 1;
  return { x: p.x, y: p.y, dx: dx / n, dy: dy / n };
}

/** Spec 010 §3.1: the hero's open S path and where its eight stops sit (fractions of path length). */
export const COURSE_STOP_FRACTIONS: readonly number[] = [0.07, 0.19, 0.31, 0.43, 0.57, 0.69, 0.81, 0.93];
